/* eslint-disable @typescript-eslint/naming-convention */
import {
  ComponentInternalInstance,
  computed,
  defineComponent,
  getCurrentInstance,
  onMounted,
  ref,
  watch,
} from "vue-demi";
import audienceSize from "./audience-size.vue";
import BytedIconTipsOne from "@byte-design/vue-icons/icons/TipsOne";
import HeaderStep from "@/components/header-step.vue";
import HeaderStepTitle from "@/components/header-step-title.vue";
import HeaderNav from "@/components/header-nav.vue";
import Popup from "@/components/popup/popup.vue";
import PopupTreeOld from "@/components/popup-tree-old.vue";
import formHeader from "src/components/common/form-header.vue";
import Tree from "@/components/form/tree-new.vue";
import helpCenter from "@/components/help-center.vue";
import CreationFooter from "@/components/creation-footer.vue";
import store from "./../../store/index";

import { genderLabels, languageOpts, NO_LIMIT } from './../../const/fastCreation';
import * as tool from './../../utils/tool';
import { INode } from './../../store/modules/audience/index';
import BytedIconDoubleDown from '@byte-design/vue-icons/icons/DoubleDown';
import BytedIconDoubleUp from '@byte-design/vue-icons/icons/DoubleUp';
import i18n from './../../i18n';
import HeaderStepNew from '@/components/header-step-new.vue';
import audienceBlock from './blocks/index.vue';
import Placement from './blocks/placement-block.vue';
import ads_creative from './../../store/modules/ads_creative/index';
import { saveDraft } from './../globalActions';
import { getters as creativeGetters } from './../../store/modules/ads_creative/state';
import mutations from './../../store/modules/ads_creative/mutations';
import { AutomatedTargetingType } from './../../store/modules/ads_creative/const';
import { interestOption, locationAreas, userEstimate } from './../../service/index';
import { formatAudienceV2 } from './../../utils/fastCreationFormat';
import searchIcon from '@/assets/images/search-icon.png';
import teaTracker from './../../tea-tracker';
import { TEA_EVENT } from './../../const/state/TeaNames';
import { reportXFmp } from './../../plugin/perfMeasurePlugin';
import { useObjectiveHooks } from './../../hooks/creation/objective/objectiveStatus';
import { AUDIENCE_SELECTION_OPTIONS, INFO_TIPS } from './const';
import SmartInterestsAndBehaviors from '../../components/smart-interests-and-behaviors/index.vue';
import { showSmartInterestToggle } from './utils';
import { SelfServeAdInfoTips } from '@ad/self-serve-sdk-vue';
import { CampaignObjectiveType } from "@ad/tt_ads_common_enums";
import { formatNumberByLangCodeUtil } from './../dashboard/perf/utils/index';
import account from './../../store/modules/account/index';

const name = "audience";
export default defineComponent({
  name,
  components: {
    Tree,
    Popup,
    HeaderNav,
    formHeader,
    helpCenter,
    HeaderStep,
    audienceBlock,
    CreationFooter,
    HeaderStepTitle,
    BytedIconTipsOne,
    BytedIconDoubleDown,
    BytedIconDoubleUp,
    HeaderStepNew,
    audienceSize,
    PopupTreeOld,
    Placement,
    SmartInterestsAndBehaviors,
    SelfServeAdInfoTips,
  },
  setup(props, cxt) {
    const languageTree = ref();
    const langSearchText = ref("");
    const { proxy: _this } = getCurrentInstance() as ComponentInternalInstance;
    const locationOptionsStore = ref([]);
    const defCounty = ref([]);
    const tempLangOptions = ref(languageOpts);
    const showMore = ref(false);
    const showInterest = ref(false);
    const showLangPopup = ref(false);
    const showDemographic = ref(false);
    const loadingETA = ref(true);
    const isWritable = ref(true);
    const audienceNotReady = ref(true);
    const estimate = ref(0);
    const componentWidth = ref(0);
    const buttonWidth = ref(0);
    const domRef = ref(null);
    const isShowLimitedLocationNotice = ref(false);
    const { isLead, isTraffic, currentObjectiveType } = useObjectiveHooks();
    const accountInfo = computed(() => store.state.account.accountInfo);
    const isSAMAccount = computed(() => account.state.accountInfo.feature_gating_user_tags.includes('state_affiliate_media_country_restrict'));
    const objective = computed(
      () => creativeGetters.campaignFormData.value.objectiveType,
    );
    const selectedPangle = ref(
      creativeGetters.audienceFormData.value.placement.includes(9000),
    );
    const selectedTikTok = ref(
      creativeGetters.audienceFormData.value.placement.includes(3000),
    );
    const location = computed(
      () => creativeGetters.audienceFormData.value.location,
    );
    const placement = computed(
      () => creativeGetters.audienceFormData.value.placement,
    );
    const userCount = ref(creativeGetters.audienceFormData.value.userCount);
    const targetType = computed(
      () => creativeGetters.audienceFormData.value.targetType,
    );
    watch(creativeGetters.audienceFormData, (newVal) => {
      const newUserCount = creativeGetters.audienceFormData.value.userCount;
      const [lowerLimit, upperLimit = 0] = newUserCount.split('-').map(limit => limit.replaceAll(',', ''));
      const NO_PRECISION = 0;
      userCount.value = `${formatNumberByLangCodeUtil(lowerLimit, NO_PRECISION)}-${formatNumberByLangCodeUtil(
        upperLimit,
        NO_PRECISION,
      )}`;
    });
    
    const getAudience = (): any => {
      if (targetType.value === AutomatedTargetingType.ON) {
        return {
          language_list: [],
          gender: "0",
          age: [],
          ad_tag_v2: [],
          country:
            location.value.country ||
            ads_creative.state.audience.location.value.default,
          province: [],
          city: [],
          districts: [],
          particle_locations:
            ads_creative.state.audience.location.value.default,
        };
      }
      return formatAudienceV2(creativeGetters.audienceFormData.value);
    };
    const getRealtimeETA = async (): Promise<any> =>
      await userEstimate({
        audience: getAudience(),
        inventory_flow: ads_creative.state.audience.placement.value,
        objective_type:
          ads_creative.getters.campaignFormData.value.objectiveType,
      });
    const updateETA = async (): Promise<void> => {
      loadingETA.value = true;
      try {
        // if (!location.value.country.length) throw Error;
        const { data } = await getRealtimeETA();
        audienceNotReady.value = false;
        isWritable.value = data.is_writable;
        estimate.value = data.estimate;
        mutations.setUserCount(data?.user_count);
      } catch (e) {
        isWritable.value = false;
        mutations.setUserCount("0");
      } finally {
        loadingETA.value = false;
      }
    };
    const filterCountry = (nodes: INode[]): Array<INode> => {
      if (!nodes) {
        return [];
      }
      const filterFn = (ns: INode[], level = 1): Array<INode> => {
        const data = [];
        for (const node of ns) {
          if (node.status === 1) {
            // 状态为 1 的国家/地区才可用
            node.children = filterFn(node.children || [], level + 1);
            data.push(node);
          }
        }
        return data;
      };
      return filterFn(nodes, 1);
    };
    const getLocationMap = (locations): any => {
      const options = locations;
      const countryCityMap = filterCountry(options);
      const root = { children: options };
      tool.walkTreeBFS({
        root,
        handler: (node: INode) => {
          node.label = node.translation;
          node.value = node.id;
          node.disabled = Boolean(node.disabled);
        },
      });
      return countryCityMap;
    };
    const locationsMap = ref([]);
    watch([selectedPangle, selectedTikTok], (newVal) => {
      const res = [];
      if (newVal[0]) {
        res.push(9000);
      }
      if (newVal[1]) {
        res.push(3000);
      }
      mutations.setPlacement(res);
    });

    watch(
      [
        targetType,
        selectedPangle,
        selectedTikTok,
        showInterest,
        showLangPopup,
        showDemographic,
      ],
      async () => {
        await updateETA();
      },
    );
    const handleInterestClick = (value): void => {
      showInterest.value = value;
    };
    const handleNextStep = (router, route): void => {
      if (!location.value?.country || !placement.value.length) {
        return;
      }
      _this.$Loading.show();
      saveDraft();
      teaTracker.trackEvent(TEA_EVENT.CONTINUE, {
        page: "audience",
        objective_type: currentObjectiveType.value,
      });
      router.push({ name: "budget", query: { ...route.query } });
    };
    const language = computed(
      () => creativeGetters.audienceFormData.value.language,
    );
    const languageLabels = (): any => {
      const languageValue = language?.value?.value || [];
      const languageLabelContent = languageValue?.map(
        (lang) => i18n.t(`language_${lang}`) as string,
      );
      return language.value?.value?.[0] === NO_LIMIT.value
        ? i18n.t("common_no_limit")
        : languageLabelContent.join(", ");
    };
    const age = computed(() => creativeGetters.audienceFormData.value.age);
    const gender = computed(
      () => creativeGetters.audienceFormData.value.gender,
    );
    const interest = computed(
      () => creativeGetters.audienceFormData.value.interest,
    );
    const interestOptions = computed(
      () => creativeGetters.audienceFormData.value.interestOption,
    );
    const demographicsPlaceholder = computed(() => {
      const genderPlaceholder = i18n.t(genderLabels[gender.value.value]);
      const AgePlaceholder =
        age.value?.value?.[0] === "NO_LIMIT"
          ? i18n.t("common_no_limit")
          : age.value?.value?.join(", ");
      return `${i18n.t("common_gender")}: ${genderPlaceholder} | ${i18n.t(
        "common_age",
      )}: ${AgePlaceholder} | ${i18n.t(
        "common_languages",
      )}: ${languageLabels()}`;
    });
    const interestPlaceholder = computed(() => {
      if (interest.value.value[0] === "NO_LIMIT") {
        return i18n.t("common_no_limit");
      }
      return interest.value.value.join(", ");
    });
    const ifAutoAudience = computed(
      () => targetType.value === AutomatedTargetingType.ON,
    );
    const handleUpdate = (target): void => {
      target === AutomatedTargetingType.OFF
        ? teaTracker.trackEvent(TEA_EVENT.USER_CLICK_CUSTOM_AUDIENCE)
        : teaTracker.trackEvent(TEA_EVENT.USER_CLICK_AUTO_TARGET);
      mutations.setTarget(target);
    };
    const handleDemographicsClick = (willShowDemographics): void => {
      if (willShowDemographics) {
        teaTracker.trackEvent(TEA_EVENT.USER_CLICK_DEMOGRAPHICS, {
          locations: "",
        });
        teaTracker.trackEvent(TEA_EVENT.DISPLAY_DEMOGRAPHICS);
      } else {
        teaTracker.trackEvent(TEA_EVENT.USER_CONFIRM_DEMOGRAPHICS);
      }
      showDemographic.value = willShowDemographics;
    };
    const onGenderChange = (newGender): void => {
      teaTracker.trackEvent(TEA_EVENT.USER_CLICK_GENDER, {
        gender: newGender,
      });
      mutations.setGender(newGender);
    };
    const handleShowMoreOrLess = () => {
      showMore.value
        ? teaTracker.trackEvent(TEA_EVENT.USER_CLICK_SHOW_LESS)
        : teaTracker.trackEvent(TEA_EVENT.USER_CLICK_SHOW_MORE);
      showMore.value = !showMore.value;
    };

    const onAgeChange = (newAgeOption): void => {
      teaTracker.trackEvent(TEA_EVENT.USER_CLICK_AGE, {
        age: newAgeOption,
      });
      const oldVal = age.value.value;
      if (
        newAgeOption &&
        newAgeOption.length > 0 &&
        newAgeOption.indexOf(NO_LIMIT.value) > -1 &&
        oldVal &&
        oldVal.indexOf(NO_LIMIT.value) === -1
      ) {
        const value = [NO_LIMIT.value];
        mutations.setAgeOption(value);
        return;
      } else if (newAgeOption && newAgeOption.length > 0) {
        if (newAgeOption.indexOf(NO_LIMIT.value) !== -1) {
          (newAgeOption as any).splice(newAgeOption.indexOf(NO_LIMIT.value), 1);
        }
        mutations.setAgeOption(newAgeOption);
      }
    };

    const handleClick = (): void => {
      if (!showLangPopup.value) {
        teaTracker.trackEvent(TEA_EVENT.USER_CLICK_LANGUAGE_DROPDOWN);
        teaTracker.trackEvent(TEA_EVENT.DISPLAY_LANGUAGE_POPUP);
      }
      showLangPopup.value = !showLangPopup.value;
    };
    const treeNodesMap = computed(() => {
      const result = {};
      const root = { children: language.value.options };

      tool.walkTreeBFS({
        root,
        handler(node) {
          if (tool.isDef(node.value)) {
            result[node.value] = node;
            (node.children || [])?.forEach((child) => {
              child.parentId = node.value; // 父节点id
            });
          }
        },
      });
      // this.$emit('tree-map', result)
      return result;
    });
    const hideLangPop = (): void => {
      tempLangOptions.value = languageOpts;
      showLangPopup.value = false;
    };

    const updateInterest = (values): void => {
      mutations.setInterest(values?.mergedValues || []);
      updateETA();
    };
    const langPopConfirm = (selectedLangs): void => {
      cxt.emit("input", selectedLangs);
      cxt.emit("change", selectedLangs);
      mutations.setLanguages(selectedLangs);
      teaTracker.trackEvent(TEA_EVENT.USER_CONFIRM_LANGUAGE, {
        languages: selectedLangs,
      });
      hideLangPop();
    };
    const handleLanguageSearchReport = () => {
      teaTracker.trackEvent(TEA_EVENT.USER_CLICK_SEARCH_BOX_ON_LANGUAGE);
    };
    const onSearch = (inputVal: string | number): void => {
      if (inputVal === "") {
        tempLangOptions.value = languageOpts;
        return;
      }
      tempLangOptions.value = language.value.options.filter((lang) =>
        lang.label.toLowerCase().includes(inputVal.toString().toLowerCase()),
      );
    };

    const locationOptions = (): any => {
      const { options } = location.value;
      const countryCityMap = filterCountry(options);
      const root = { children: options };
      tool.walkTreeBFS({
        root,
        handler: (node: INode) => {
          node.label = node.translation;
          node.value = node.id;
          node.disabled = Boolean(node.disabled);
        },
      });
      return countryCityMap;
    };
    const filterOthers = (mergedValues, throughedValues): any => {
      let m = mergedValues;
      const t = throughedValues;
      const parentMap = {};
      const childrenMap = {};
      const othersCountryMap = {};

      const validLoc = (v: string | number): boolean =>
        !String(v).includes("-other");

      // 建立tree父子关系图
      function buildDFS(node, countryId): void {
        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            parentMap[child.id] = node.id;

            if (!childrenMap[node.id]) {
              childrenMap[node.id] = [];
            }
            if (validLoc(child.id)) {
              childrenMap[node.id].push(child.id);
            } else {
              othersCountryMap[child.id] = countryId;
            }
            buildDFS(child, countryId);
          }
        }
      }

      // 利用 tree 关系图将 包含 others 的 country 及其下面的所有state city districty从组件树中移除
      function removeDFS(node, level): any {
        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            removeDFS(child, level + 1);
          }
        }
        m = m.filter((n) => n !== node.id);
        t[level] = t[level].filter((n) => n !== node.id);
      }

      for (const countryNode of locationOptions()) {
        buildDFS(countryNode, countryNode.id);
      }

      // 检查国家下面是否选中others，否则过滤
      const toRemoveCountry = new Set();
      throughedValues[2]?.forEach((n) => {
        if (!validLoc(n)) {
          toRemoveCountry.add(othersCountryMap[n]);
        }
      });
      throughedValues[3]?.forEach((n) => {
        if (!validLoc(n)) {
          toRemoveCountry.add(othersCountryMap[n]);
        }
      });

      const toRemoveCountryArray = Array.from(toRemoveCountry);
      for (const countryNode of locationOptions()) {
        if (toRemoveCountryArray.includes(countryNode.id)) {
          removeDFS(countryNode, 0);
        }
      }

      m = m.filter(validLoc);
      throughedValues[0] = throughedValues[0]?.filter(validLoc);
      throughedValues[1] = throughedValues[1]?.filter(validLoc);
      throughedValues[2] = throughedValues[2]?.filter(validLoc);
      throughedValues[3] = throughedValues[3]?.filter(validLoc);
      return [m, t];
    };
    const onLocationMap = (treeMap: Record<number, any>): void => {
      mutations.setLocationsVal(treeMap);
    };
    const updateLocation = (values: {
      mergedValues: Array<string | number>;
      throughedValues: Array<Array<string | number>>;
    }): void => {
      let { mergedValues = [], throughedValues = [] } = values;
      try {
        const res = filterOthers(mergedValues, throughedValues);
        mergedValues = res[0];
        throughedValues = res[1];
      } catch (e) {
        console.log("filterOthers error:", e);
      }
      mutations.setLocationsVal(mergedValues);
      mutations.setLocationsCountry(throughedValues[0]);
      mutations.setLocationsProvince(throughedValues[1]);
      mutations.setLocationsCity(throughedValues[2]);
      mutations.setLocationsDistricts(throughedValues[3]);
      updateETA();
      teaTracker.trackEvent(TEA_EVENT.USER_CONFIRM_LOCATION, {
        location: mergedValues,
      });
    };

    const travelTree = (nodes: INode[]): INode[] => {
      for (let i = 0; i < nodes.length; i++) {
        const area = nodes[i];
        area.label = area.i18n_value;
        area.value = area.id;
        if (area.children) {
          area.children = travelTree(area.children);
        }
      }
      return nodes;
    };
    const updateETAWithDefault = async (locations): Promise<any> => {
      _this.$Loading.show();
      const { id: countryId = "" } = locations?.find(
        (item) =>
          `${item.code}`.toUpperCase() ===
          accountInfo.value.country.toUpperCase(),
      ) || { id: "" };
      if (countryId) {
        const def = [countryId];
        defCounty.value = def;
        if (!location.value.value.length) {
          mutations.setLocationsVal(def);
          mutations.setLocationsCountry(def);
          mutations.setLocationsDefault(def);
          await updateETA();
        }
      }
      _this.$Loading.hide();
    };
    const initLocations = async () => {
      const areas = await locationAreas({
        inventory_flow: JSON.stringify([3000, 9000]),
        objective_type: objective.value,
      })
      const locations = areas?.sort((a: any, b: any) => a.translation.charCodeAt(0) - b.translation.charCodeAt(0));
      locationOptionsStore.value = locations;
      locationsMap.value = getLocationMap(locations);
      function findDFS(locations, value) {
        for (let location of locations || []) {
          if (location?.status === 2) {
            continue;
          }
          if (location.value == value) {
            return true;
          }
          if (location?.children?.length) {
            const result = findDFS(location.children, value);
            if (result) return result;
          }
        }
        return false;
      }
      if (isSAMAccount.value) {
        if (location.value?.value.length) {
          const availableLocations = [];
          const draftLocations = location.value?.value.length;
          for (let place of location.value.value) {
            const isAvailable = findDFS(locations, place);
            if (isAvailable) {
              availableLocations.push(place);
            } else if (location.value.city === place) {
              mutations.setLocationsCity([]);
            } else if (location.value.province === place) {
              mutations.setLocationsProvince([]);
            } else if (location.value.districts === place) {
              mutations.setLocationsDistricts([]);
            }
          }
          if (draftLocations !== availableLocations.length) {
            const { id: countryId = "" } = locations?.find(
              (item) =>
                `${item.code}`.toUpperCase() ===
                accountInfo.value.country.toUpperCase(),
            ) || { id: "" };
            if (!availableLocations.length) {
              availableLocations.push(countryId);
            }
            mutations.setLocationsVal(availableLocations);
            mutations.setLocationsCountry([countryId]);
            isShowLimitedLocationNotice.value = true;
          }
        }
      }
      mutations.setLocationOptions(locations);
      await updateETAWithDefault(locations);
    };
    onMounted(async () => {
      reportXFmp();
      _this.$Loading.hide();
      componentWidth.value = domRef.value?.clientWidth ?? 390;
      buttonWidth.value = (componentWidth.value - 38 - 32) / 3;
      isLead.value ? mutations.setAgeToLead() : mutations.setAgeToNormal();
      teaTracker.trackEvent(TEA_EVENT.DISPLAY_AUDIENCE_PAGE, {
        source: "new",
        objective_type: currentObjectiveType.value,
      });
      interestOption().then((curInterestOption) => {
        const noLimitedOption = [
          {
            i18n_value: i18n.t("common_no_limit"),
            value: "NO_LIMIT",
            industry_types: [1, 2, 3],
            inventory: [],
            id: "NO_LIMIT",
            label: i18n.t("common_no_limit"),
            children: [],
          },
        ];

        mutations.setInterestOptions(
          travelTree(noLimitedOption.concat(curInterestOption)),
        );
      });
      try {
        _this.$Loading.show();
        await initLocations();
        _this.$Loading.hide();
      } catch (e) {
        console.log('error inside initLocations: ', e);
        _this.$Loading.hide();
      }
      await updateETA();
    });
    const languageTitle = computed(() =>
      _this.$t("module_audience_languages_selected", {
        number: languageTree.value?.selected.length || 1,
      }),
    );

    const selectOptions = computed(() => {
      if (
        currentObjectiveType.value === CampaignObjectiveType.TRAFFIC ||
        currentObjectiveType.value === CampaignObjectiveType.ENGAGEMENT
      ) {
        return AUDIENCE_SELECTION_OPTIONS.filter(
          (item) => item.blockId === AutomatedTargetingType.OFF,
        );
      } else return AUDIENCE_SELECTION_OPTIONS;
    });
    const isShowU18Tips = computed(
      () =>
        (currentObjectiveType.value === CampaignObjectiveType.LEADADS ||
          currentObjectiveType.value === CampaignObjectiveType.CONVERSION) &&
        targetType.value === AutomatedTargetingType.ON,
    );
    return {
      age,
      name,
      gender,
      domRef,
      estimate,
      isWritable,
      location,
      placement,
      showMore,
      interest,
      onSearch,
      buttonWidth,
      updateInterest,
      ifAutoAudience,
      userCount,
      locationsMap,
      audienceNotReady,
      handleLanguageSearchReport,
      language,
      targetType,
      handleClick,
      onAgeChange,
      treeNodesMap,
      handleUpdate,
      onLocationMap,
      updateLocation,
      locationOptions,
      interestOptions,
      selectOptions,
      hideLangPop,
      selectedPangle,
      selectedTikTok,
      langSearchText,
      showLangPopup,
      langPopConfirm,
      languageLabels,
      onGenderChange,
      showDemographic,
      tempLangOptions,
      handleDemographicsClick,
      demographicsPlaceholder,
      handleShowMoreOrLess,
      handleInterestClick,
      showInterest,
      loadingETA,
      isTraffic,
      handleNextStep,
      interestPlaceholder,
      languageTree,
      languageTitle,
      searchIcon,
      isShowU18Tips,
      showSmartInterestToggle,
      INFO_TIPS,
      isShowLimitedLocationNotice,
    };
  },
});
